import { isEqual } from "lodash-es";

import { FieldId } from "api/generated/model";
import { diffToPresence } from "utils/diff";

import {
    Field,
    FieldAction,
    FieldGroup,
    InsertableField,
    UpdatableField,
    UpdatableFieldGroup,
} from "../models";

export function diffFieldGroup<FieldType, FieldValue>(
    source: FieldGroup<FieldType, FieldValue>,
    update: FieldGroup<FieldType, FieldValue>
): UpdatableFieldGroup<FieldType, FieldValue> {
    const actions: Record<FieldId, FieldAction<FieldType, FieldValue>> = {};
    let hasOrderChange = false;

    for (let index = 0; index < update.fields.length; index++) {
        const updateField = update.fields[index];
        const insertableUpdateField: InsertableField<FieldType, FieldValue> = {
            blobIds: updateField.blobIds,
            condition: updateField.condition,
            fieldType: updateField.fieldType,
            instructions: updateField.instructions,
            mandatory: updateField.mandatory,
            title: updateField.title,
        };
        const sourceFieldIndex = source.fields.findIndex(
            (field) => field.id === updateField.id
        );
        if (sourceFieldIndex === -1) {
            actions[updateField.id] = {
                type: "Insert",
                value: insertableUpdateField,
            };
            hasOrderChange = true;
        } else {
            hasOrderChange = hasOrderChange || index !== sourceFieldIndex;
            const sourceField = source.fields[sourceFieldIndex];
            if (!isEqual(sourceField, updateField)) {
                actions[updateField.id] = {
                    type: "Update",
                    value: diffField(sourceField, updateField),
                };
            }
        }
    }

    for (const sourceField of source.fields) {
        if (
            update.fields.findIndex((field) => field.id === sourceField.id) ===
            -1
        ) {
            actions[sourceField.id] = {
                type: "Delete",
            };
            hasOrderChange = true;
        }
    }

    const diff: UpdatableFieldGroup<FieldType, FieldValue> = {
        fields: { actions, orderedIds: undefined },
    };

    if (hasOrderChange) {
        diff.fields.orderedIds = update.fields.map((f) => f.id);
    }

    return diff;
}

function diffField<FieldType, FieldValue>(
    source: Field<FieldType, FieldValue>,
    update: Field<FieldType, FieldValue>
): UpdatableField<FieldType, FieldValue> {
    const diff: UpdatableField<FieldType, FieldValue> = {
        blobs: undefined,
        condition: undefined,
        fieldType: undefined,
        instructions: undefined,
        mandatory: undefined,
        title: undefined,
    };

    if (!isEqual(source.title, update.title)) {
        diff.title = update.title;
    }
    if (!isEqual(source.instructions, update.instructions)) {
        diff.instructions = update.instructions;
    }
    if (!isEqual(source.fieldType, update.fieldType)) {
        diff.fieldType = update.fieldType;
    }
    if (!isEqual(source.mandatory, update.mandatory)) {
        diff.mandatory = update.mandatory;
    }
    if (!isEqual(source.condition, update.condition)) {
        diff.condition = update.condition;
    }
    const sourceBlobIds = new Set(source.blobIds);
    const updateBlobIds = new Set(update.blobIds);
    if (!isEqual(sourceBlobIds, updateBlobIds)) {
        diff.blobs = diffToPresence(sourceBlobIds, updateBlobIds);
    }

    return diff;
}
